import { useEffect, useId, useRef, useState } from 'react'
import { budgetOptimizationService } from '../services/api'
import { useNotifications } from '../contexts/NotificationContext'
import {
  actionLabel,
  allActionableSelected,
  apiErrorMessage,
  applySelectionHint,
  budgetNotify,
  applyFixtureTranslations,
  displayExplanatoryText,
  languageBanner,
  formatPercentFr,
  interpretOptimizeResponse,
  priorityLabel,
  resultModelLabel,
  scenarioIdToApply,
  scenarioTitle,
  selectableSuggestionIds,
  successNotificationFor,
  synergyCertaintyLabel,
  synergyRelationLabel,
} from '../utils/budgetOptimizationOutcome'
import { formatEuroFr } from '../utils/editProductOutcome'
import { useProductCardImage } from '../hooks/useProductCardImage'
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion'
import Lottie from './Lottie'
import GlassOverlay from './GlassOverlay'
import LiquidGlassApplyButton from './LiquidGlassApplyButton'
import LiquidGlassOptimizeButton from './LiquidGlassOptimizeButton'
import LiquidGlassCancelButton from './LiquidGlassCancelButton'
import TowerLoader from './TowerLoader'
import './BudgetOptimizationModal.css'

function formatSignedEuro(value) {
  const formatted = formatEuroFr(value)
  return formatted.startsWith('-') ? `\u2212${formatted.slice(1)}` : formatted
}

function gapToneFromValues(total, budget) {
  const gap = (Number(total) || 0) - (Number(budget) || 0)
  if (gap > 0.009) return { gap, tone: 'over', caption: 'Au-dessus du budget' }
  if (gap < -0.009) return { gap, tone: 'under', caption: 'En dessous du budget' }
  return { gap, tone: 'even', caption: 'À l’équilibre' }
}

function storedResultKey(cartId) {
  return `budget-opt-last-result:${cartId || 'unknown'}`
}

function SynergyProductThumb({ item }) {
  const { src, onError } = useProductCardImage(item)
  if (!src) return <span className="synergy-product-fallback" aria-hidden="true" />
  return <img src={src} alt="" onError={onError} />
}

function SuggestionItemThumb({ item }) {
  const { src, onError } = useProductCardImage(item)
  if (!src) return <div className="suggestion-image is-empty" aria-hidden="true" />
  return (
    <div className="suggestion-image">
      <img src={src} alt="" onError={onError} />
    </div>
  )
}

function CloseGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  )
}

function LeafGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <path d="M5 19c8-1 13-8 14-14-6 1-13 6-14 14Z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M8 16c2.5-2 5.5-3.5 9-4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  )
}

function LayersGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <path d="M12 4l8 4-8 4-8-4 8-4Z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M4 12l8 4 8-4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 16l8 4 8-4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function InfoGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <circle cx="12" cy="12" r="8.25" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path d="M12 11.2V17M12 8h.01" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}

function KeepGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path d="M5 12.5l4.2 4.2L19 7.5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function RemoveGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path d="M5 7h14M9 7V5.8A1.8 1.8 0 0 1 10.8 4h2.4A1.8 1.8 0 0 1 15 5.8V7m-7.2 0 0.7 12.2A1.6 1.6 0 0 0 10.1 21h3.8a1.6 1.6 0 0 0 1.6-1.8L16.2 7" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function ReduceGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path d="M5 12h14" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
      <path d="M16 8l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function ReplaceGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path d="M7 7h9.5a3 3 0 0 1 0 6H8" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10 4 7 7l3 3M14 14l3 3-3 3" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function ActionGlyph({ action }) {
  if (action === 'remove') return <RemoveGlyph />
  if (action === 'reduce_quantity') return <ReduceGlyph />
  if (action === 'replace') return <ReplaceGlyph />
  return <KeepGlyph />
}

function SynergyBlock({ synergy, itemsMap }) {
  const products = (synergy.items || [])
    .map((itemId) => itemsMap.get(itemId))
    .filter(Boolean)
  const title = displayExplanatoryText(synergy.title, synergy.title_source)
  const summary = displayExplanatoryText(synergy.summary || synergy.reason, synergy.summary_source || synergy.reason_source)
  const description = displayExplanatoryText(synergy.description, synergy.description_source)
  const reason = displayExplanatoryText(synergy.reason, synergy.reason_source)
  const consequence = displayExplanatoryText(synergy.removal_consequence, synergy.removal_consequence_source)
  const recommendation = displayExplanatoryText(synergy.recommendation, synergy.recommendation_source)
  const limits = displayExplanatoryText(synergy.recommendation_limits, synergy.recommendation_limits_source)
  const relation = synergyRelationLabel(synergy.relation)
  const certainty = synergyCertaintyLabel(synergy.certainty)

  return (
    <details className={`synergy-block is-${synergy.certainty || 'hypothesis'}`}>
      <summary className="synergy-summary">
        <span className="synergy-summary-copy">
          <strong>{title || summary || 'Relation entre articles du panier'}</strong>
          <span className="synergy-meta">
            {relation ? <span>{relation}</span> : null}
            <span>{certainty}</span>
          </span>
        </span>
      </summary>
      <div className="synergy-details">
        {title && summary && title !== summary ? (
          <p><strong>Synthèse.</strong> {summary}</p>
        ) : null}
        {description ? (
          <p><strong>Description.</strong> {description}</p>
        ) : null}
        {products.length > 0 && (
          <ul className="synergy-products">
            {products.map((item) => (
              <li key={item.id} className="synergy-product">
                <SynergyProductThumb item={item} />
                <span>{item.name}</span>
              </li>
            ))}
          </ul>
        )}
        {reason ? (
          <p>
            <strong>Justification.</strong> {reason}
          </p>
        ) : null}
        {consequence ? (
          <p>
            <strong>Conséquence d’un retrait ou d’une réduction.</strong> {consequence}
          </p>
        ) : null}
        {recommendation ? (
          <p>
            <strong>Recommandation.</strong> {recommendation}
          </p>
        ) : null}
        {limits ? (
          <p>
            <strong>Limites.</strong> {limits}
          </p>
        ) : null}
      </div>
    </details>
  )
}

function BudgetOptimizationModal({
  isOpen,
  onClose,
  cartId,
  currentBudget,
  currentTotal,
  currentItems,
  onOptimizationApplied,
  previewResult = null,
  previewStep = null,
}) {
  const { addNotification } = useNotifications()
  const titleId = useId()
  const closeRef = useRef(null)
  const reducedMotion = usePrefersReducedMotion()

  const [step, setStep] = useState('input')
  const [targetBudget, setTargetBudget] = useState('')
  const [modelType, setModelType] = useState('economical')
  const [optimizationResult, setOptimizationResult] = useState(null)
  const [selectedScenario, setSelectedScenario] = useState(null)
  const [selectedSuggestions, setSelectedSuggestions] = useState(new Set())
  const [loadingAnimation, setLoadingAnimation] = useState(null)
  const [itemsMap, setItemsMap] = useState(new Map())
  const [translating, setTranslating] = useState(false)
  const [translationRetries, setTranslationRetries] = useState(0)

  useEffect(() => {
    fetch('/Target.json')
      .then((res) => res.json())
      .then((data) => setLoadingAnimation(data))
      .catch((err) => console.error('Erreur chargement animation:', err))
  }, [])

  useEffect(() => {
    if (!isOpen) return
    if (previewStep === 'loading') {
      setStep('loading')
      setOptimizationResult(null)
      setSelectedScenario(null)
    } else if (previewResult) {
      setStep('results')
      setOptimizationResult(previewResult)
      const balancedScenario = previewResult.scenarios?.find((s) => s.type === 'balanced')
      setSelectedScenario(balancedScenario || previewResult.scenarios?.[0] || null)
    } else if (optimizationResult?.cart_id === cartId) {
      setStep('results')
    } else {
      setStep('input')
      setOptimizationResult(null)
      setSelectedScenario(null)
    }
    setModelType('economical')
    setSelectedSuggestions(new Set())
    setTranslationRetries(0)
  }, [isOpen, previewResult, previewStep, cartId])

  useEffect(() => {
    if (!isOpen || previewResult || previewStep || optimizationResult) return undefined
    const resultId = sessionStorage.getItem(storedResultKey(cartId))
    if (!resultId) return undefined
    let cancelled = false
    budgetOptimizationService.getHistory(cartId)
      .then((history) => {
        if (cancelled) return
        const restored = (history?.results || []).find((row) => row.result_id === resultId)
        if (!restored?.scenarios?.length) return
        setOptimizationResult(restored)
        setModelType(restored.model_option || 'economical')
        const balanced = restored.scenarios.find((row) => row.type === 'balanced')
        setSelectedScenario(balanced || restored.scenarios[0])
        setStep('results')
      })
      .catch((error) => {
        console.warn('[BUDGET_LANGUAGE] restauration impossible', error?.response?.status || error?.name)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, previewResult, previewStep, optimizationResult, cartId])

  useEffect(() => {
    if (isOpen && currentBudget != null && currentBudget !== '') {
      setTargetBudget(currentBudget.toString())
    }
  }, [isOpen, currentBudget])

  useEffect(() => {
    if (currentItems && currentItems.length > 0) {
      const map = new Map()
      currentItems.forEach((item) => {
        map.set(item.id, item)
      })
      setItemsMap(map)
    }
  }, [currentItems])

  const handleClose = () => {
    onClose()
  }

  const handleOptimize = async () => {
    if (!targetBudget || parseFloat(targetBudget) <= 0) {
      addNotification(budgetNotify('error', 'Veuillez entrer un budget cible valide'))
      return
    }

    setStep('loading')

    try {
      const result = await budgetOptimizationService.optimizeBudget(
        cartId,
        parseFloat(targetBudget),
        modelType,
      )
      const outcome = interpretOptimizeResponse(result)
      if (!outcome.ok) {
        addNotification(budgetNotify('error', outcome.message))
        setStep('input')
        return
      }

      setOptimizationResult(result)
      if (result.result_id) {
        sessionStorage.setItem(storedResultKey(cartId), result.result_id)
      }
      setStep('results')

      const balancedScenario = result.scenarios?.find((s) => s.type === 'balanced')
      if (balancedScenario) {
        setSelectedScenario(balancedScenario)
      }

      addNotification(successNotificationFor(outcome))
    } catch (error) {
      console.error('Erreur optimisation:', error)
      addNotification(budgetNotify(
        'error',
        apiErrorMessage(error, 'Erreur lors de l\'optimisation'),
      ))
      setStep('input')
    }
  }

  const handleApplyScenario = async () => {
    if (previewResult) {
      addNotification(budgetNotify('info', 'Aperçu uniquement : aucun scénario n’est appliqué.'))
      return
    }

    if (!selectedScenario) {
      addNotification(budgetNotify('error', 'Veuillez sélectionner un scénario'))
      return
    }

    const target = scenarioIdToApply(selectedScenario)
    if (!target.ok) {
      addNotification(budgetNotify('error', target.message))
      return
    }

    const suggestionsToApply = selectedSuggestions.size > 0
      ? Array.from(selectedSuggestions)
      : null

    setStep('applying')

    try {
      await budgetOptimizationService.applyScenario(
        cartId,
        target.id,
        suggestionsToApply,
      )

      addNotification(budgetNotify('success', 'Optimisation appliquée avec succès.'))

      if (onOptimizationApplied) {
        onOptimizationApplied()
      }

      handleClose()
    } catch (error) {
      console.error('Erreur application scénario:', error)
      addNotification(budgetNotify(
        'error',
        apiErrorMessage(error, 'Erreur lors de l\'application'),
      ))
      setStep('results')
    }
  }

  const handleTranslateExplanations = async () => {
    if (!optimizationResult || translating || translationRetries >= 1) return
    setTranslationRetries((count) => count + 1)
    setTranslating(true)
    try {
      if (previewResult) {
        const translated = applyFixtureTranslations(optimizationResult)
        setOptimizationResult(translated)
        const selectedType = selectedScenario?.type
        const nextSelected = translated.scenarios?.find((row) => row.type === selectedType)
          || translated.scenarios?.[0]
          || null
        setSelectedScenario(nextSelected)
        addNotification(budgetNotify('success', 'Commentaires traduits pour l’aperçu, sans relancer l’optimisation.'))
        return
      }
      const translated = await budgetOptimizationService.translateExplanations(
        cartId,
        optimizationResult,
        modelType,
        selectedScenario?.id || null,
      )
      setOptimizationResult(translated)
      if (translated.result_id) {
        sessionStorage.setItem(storedResultKey(cartId), translated.result_id)
      }
      const selectedType = selectedScenario?.type
      const nextSelected = translated.scenarios?.find((row) => row.type === selectedType)
        || translated.scenarios?.[0]
        || null
      setSelectedScenario(nextSelected)
      const repair = translated.language_repair || {}
      if (repair.can_retry || repair.originals_lost) {
        addNotification(budgetNotify('warning', repair.message || 'La traduction n’a pas pu être terminée.'))
      } else {
        addNotification(budgetNotify('success', 'Commentaires traduits en français.'))
      }
    } catch (error) {
      addNotification(budgetNotify(
        'error',
        apiErrorMessage(error, 'La traduction des commentaires a échoué.'),
      ))
    } finally {
      setTranslating(false)
    }
  }

  const toggleSuggestion = (itemId) => {
    const newSelected = new Set(selectedSuggestions)
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId)
    } else {
      newSelected.add(itemId)
    }
    setSelectedSuggestions(newSelected)
  }

  const toggleAllSuggestions = () => {
    if (!selectedScenario) return
    const allIds = selectableSuggestionIds(selectedScenario)
    if (allActionableSelected(selectedSuggestions, selectedScenario)) {
      setSelectedSuggestions(new Set())
    } else {
      setSelectedSuggestions(new Set(allIds))
    }
  }

  const { gap, tone, caption } = gapToneFromValues(currentTotal, currentBudget)
  const isResultsLayout = step === 'results' || step === 'loading' || step === 'applying'
  const actionableCount = selectableSuggestionIds(selectedScenario).length
  const allSelectableOn = allActionableSelected(selectedSuggestions, selectedScenario)
  const banner = step === 'results' && optimizationResult ? languageBanner(optimizationResult) : null

  return (
    <GlassOverlay
      isOpen={isOpen}
      onClose={handleClose}
      labelledBy={titleId}
      panelClassName={`budget-opt-dialog${isResultsLayout ? ' is-results' : ''}`}
      rootClassName="budget-opt-root"
      followPointer={false}
      initialFocusRef={closeRef}
    >
      <div className="budget-opt">
        <header className="budget-opt-header">
          <h2 id={titleId}>Système de Redressement Budgétaire</h2>
          <button
            ref={closeRef}
            type="button"
            className="budget-opt-close"
            onClick={handleClose}
            aria-label="Fermer"
          >
            <CloseGlyph />
          </button>
        </header>

        <div className="budget-opt-body">
          {step === 'input' && (
            <div className="budget-opt-input">
              <section className="budget-opt-section" aria-label="Synthèse budgétaire">
                <div className="budget-opt-stats">
                  <article className="budget-opt-stat">
                    <span className="budget-opt-stat-label">Budget actuel</span>
                    <span className="budget-opt-stat-value">{formatSignedEuro(currentBudget)}</span>
                  </article>
                  <article className="budget-opt-stat">
                    <span className="budget-opt-stat-label">Total panier</span>
                    <span className="budget-opt-stat-value">{formatSignedEuro(currentTotal)}</span>
                  </article>
                  <article className={`budget-opt-stat is-${tone}`}>
                    <span className="budget-opt-stat-label">Écart</span>
                    <span className="budget-opt-stat-value">{formatSignedEuro(gap)}</span>
                    <span className="budget-opt-stat-caption">{caption}</span>
                  </article>
                </div>
              </section>

              <section className="budget-opt-section">
                <label className="budget-opt-label" htmlFor="targetBudget">
                  Seuil de redressement cible
                </label>
                <div className="budget-opt-field">
                  <input
                    type="number"
                    inputMode="decimal"
                    id="targetBudget"
                    value={targetBudget}
                    onChange={(e) => setTargetBudget(e.target.value)}
                    placeholder="Ex: 2000"
                    min="0"
                    step="0.01"
                  />
                  <span className="budget-opt-unit" aria-hidden="true">€</span>
                </div>
              </section>

              <section className="budget-opt-section">
                <p className="budget-opt-label" id="budget-opt-models-label">
                  Architecture d'interprétation intelligente
                </p>
                <div className="budget-opt-models" role="group" aria-labelledby="budget-opt-models-label">
                  <button
                    type="button"
                    className={`budget-opt-model${modelType === 'economical' ? ' is-selected' : ''}`}
                    onClick={() => setModelType('economical')}
                    aria-pressed={modelType === 'economical'}
                  >
                    <span className="budget-opt-model-mark" aria-hidden="true" />
                    <span className="budget-opt-model-icon"><LeafGlyph /></span>
                    <span className="budget-opt-model-copy">
                      <span className="budget-opt-model-name">Protocole d'interprétation parcimonieux</span>
                      <span className="budget-opt-model-desc">Dimensional Pioumon Frugal 12B</span>
                    </span>
                  </button>

                  <button
                    type="button"
                    className={`budget-opt-model${modelType === 'high_quality' ? ' is-selected' : ''}`}
                    onClick={() => setModelType('high_quality')}
                    aria-pressed={modelType === 'high_quality'}
                  >
                    <span className="budget-opt-model-mark" aria-hidden="true" />
                    <span className="budget-opt-model-icon"><LayersGlyph /></span>
                    <span className="budget-opt-model-copy">
                      <span className="budget-opt-model-name">Protocole d'interprétation à compréhension synergique</span>
                      <span className="budget-opt-model-desc">Dimensional Pioumon Interpreter 76B</span>
                    </span>
                  </button>
                </div>
              </section>

              <aside className="budget-opt-note">
                <span className="budget-opt-note-icon" aria-hidden="true"><InfoGlyph /></span>
                <div className="budget-opt-note-copy">
                  <strong>Descriptif de l'exécution procéduralement composée</strong>
                  <p>
                    Le modèle d'interprétation DimensionalPioumon, pourvu d'une quantité de données suffisante et d'une supervision d'apprentissage, procédera à l'élaboration d'un plan trichotiquement composé, déterminant les axes de redressement budgétaires probables, fondés sur les préceptes de la psychologie d'achat.
                  </p>
                </div>
              </aside>
            </div>
          )}

          {step === 'loading' && (
            <div className="optimization-loading">
              <div className="optimization-loading-stage" aria-hidden="true">
                <TowerLoader />
              </div>
              <div className="optimization-loading-copy">
                <h3>Analyse du panier en cours</h3>
                <div className="loading-indeterminate" role="status" aria-label="Progression indéterminée">
                  <span />
                </div>
                <p className="loading-hint">
                  Aucune étape n’est marquée comme terminée : le serveur ne transmet pas encore de progression.
                </p>
              </div>
            </div>
          )}

          {step === 'results' && optimizationResult && (
            <div className="optimization-results">
              <div className="results-header">
                <h3>Scénarios d’optimisation</h3>
                <div className="results-meta">
                  <span className="ai-model-badge">
                    <LayersGlyph />
                    {resultModelLabel(optimizationResult)}
                  </span>
                  <span className="items-count-badge">
                    {optimizationResult.items_count} articles analysés
                  </span>
                </div>
              </div>

              {optimizationResult.already_under_target && (
                <p className="budget-opt-result-note">
                  Le panier respecte déjà le seuil. Les scénarios ci-dessous sont des pistes d’ajustement, pas un dépassement à combler.
                </p>
              )}
              {optimizationResult.target_reachable === false && (
                <p className="budget-opt-result-note is-warning">
                  Aucun scénario n’atteint le seuil avec les articles existants. Aucun produit n’a été inventé pour forcer l’objectif.
                </p>
              )}
              {optimizationResult.summary ? (
                <p className="budget-opt-result-note">
                  {displayExplanatoryText(optimizationResult.summary, optimizationResult.summary_source)}
                </p>
              ) : null}
              {optimizationResult.warning ? (
                <p className="budget-opt-result-note is-warning">
                  {displayExplanatoryText(optimizationResult.warning, optimizationResult.warning_source)}
                </p>
              ) : null}
              {(optimizationResult.warnings || []).map((warning, index) => (
                <p className="budget-opt-result-note is-warning" key={`warning-${index}`}>
                  {displayExplanatoryText(
                    warning.warning || warning.description || warning.summary || warning.reason,
                    warning.warning_source || warning.description_source || warning.summary_source || warning.reason_source,
                  )}
                </p>
              ))}
              {banner ? (
                <p className={`budget-opt-result-note is-warning language-banner is-${banner.kind}`}>
                  <span>{banner.message}</span>
                  {banner.canRetry && translationRetries < 1 ? (
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={handleTranslateExplanations}
                      disabled={translating}
                    >
                      {translating ? 'Traduction…' : 'Réessayer la traduction'}
                    </button>
                  ) : null}
                  {banner.canReanalyze ? (
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => setStep('input')}
                    >
                      Relancer l’analyse
                    </button>
                  ) : null}
                </p>
              ) : null}

              <div className="scenarios-grid" role="group" aria-label="Scénarios d’optimisation">
                {optimizationResult.scenarios?.map((scenario) => {
                  const selected = selectedScenario?.type === scenario.type
                  return (
                    <button
                      key={scenario.type}
                      type="button"
                      className={`scenario-card${selected ? ' is-selected' : ''}`}
                      onClick={() => {
                        setSelectedScenario(scenario)
                        setSelectedSuggestions(new Set())
                      }}
                      aria-pressed={selected}
                    >
                      {selected ? <span className="scenario-selected-label">Sélectionné</span> : null}
                      <span className="scenario-icon" aria-hidden="true">
                        {scenario.type === 'conservative' && <img src="/Icon shield.png" alt="" className="scenario-icon-img" />}
                        {scenario.type === 'balanced' && <img src="/Icon scale balanced.png" alt="" className="scenario-icon-img" />}
                        {scenario.type === 'aggressive' && <img src="/Icon do not disturb off.png" alt="" className="scenario-icon-img" />}
                      </span>
                      <span className="scenario-name">{scenarioTitle(scenario.type)}</span>
                      <span className="scenario-metric">
                        <span className="stat-label">Articles conservés</span>
                        <span className="stat-value">{formatPercentFr(scenario.items_kept_percentage)}</span>
                      </span>
                      <span className="scenario-metric">
                        <span className="stat-label">Économies estimées</span>
                        <span className="stat-value is-savings">{formatEuroFr(scenario.savings)}</span>
                      </span>
                    </button>
                  )
                })}
              </div>

              {selectedScenario && (
                <div
                  className={`scenario-details${reducedMotion ? ' is-static' : ''}`}
                  key={selectedScenario.type}
                >
                  <div className="details-header">
                    <h4 className="suggestions-title">
                      Suggestions ({selectedScenario.suggestions?.length || 0})
                    </h4>
                    {actionableCount > 0 && (
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        onClick={toggleAllSuggestions}
                      >
                        {allSelectableOn ? 'Tout désélectionner' : 'Tout sélectionner'}
                      </button>
                    )}
                  </div>
                  <p className="suggestions-hint">{applySelectionHint(selectedSuggestions.size, actionableCount)}</p>
                  {selectedScenario.title ? (
                    <p className="suggestions-hint">
                      <strong>{displayExplanatoryText(selectedScenario.title, selectedScenario.title_source)}</strong>
                    </p>
                  ) : null}
                  {selectedScenario.summary || selectedScenario.rationale ? (
                    <p className="suggestions-hint">
                      {displayExplanatoryText(
                        selectedScenario.summary || selectedScenario.rationale,
                        selectedScenario.summary_source || selectedScenario.rationale_source,
                      )}
                    </p>
                  ) : null}
                  {selectedScenario.warning ? (
                    <p className="budget-opt-result-note is-warning">
                      {displayExplanatoryText(selectedScenario.warning, selectedScenario.warning_source)}
                    </p>
                  ) : null}

                  <div className="suggestions-list">
                    {selectedScenario.suggestions?.map((suggestion, index) => {
                      const item = itemsMap.get(suggestion.item_id)
                      const itemName = item?.name || 'Produit inconnu'
                      const itemPrice = item?.price || 0
                      const itemQuantity = item?.quantity || 1
                      const lineTotal = Number(itemPrice) * Number(itemQuantity || 1)
                      const canSelect = suggestion.action !== 'keep'
                      const checked = selectedSuggestions.has(suggestion.item_id)
                      const priority = priorityLabel(suggestion.priority)

                      return (
                        <div
                          key={`${suggestion.item_id}-${index}`}
                          className={`suggestion-item is-${suggestion.action}${checked ? ' is-checked' : ''}`}
                        >
                          {canSelect ? (
                            <label className="suggestion-check">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleSuggestion(suggestion.item_id)}
                                aria-label={`Inclure « ${itemName} » à l’application`}
                              />
                            </label>
                          ) : (
                            <span className="suggestion-check is-static" aria-hidden="true" />
                          )}

                          <SuggestionItemThumb item={item} />

                          <div className="suggestion-content">
                            <strong className="suggestion-name">{itemName}</strong>
                            <p className="suggestion-reason">{displayExplanatoryText(suggestion.reason, suggestion.reason_source)}</p>
                            {suggestion.alternative ? (
                              <p className="suggestion-alternative">{displayExplanatoryText(suggestion.alternative, suggestion.alternative_source)}</p>
                            ) : null}
                            {priority ? <p className="suggestion-priority">{priority}</p> : null}
                          </div>

                          <div className={`suggestion-action is-${suggestion.action}`}>
                            <ActionGlyph action={suggestion.action} />
                            <span>
                              {actionLabel(suggestion.action, suggestion.suggested_quantity, itemQuantity)}
                            </span>
                          </div>

                          <div className="suggestion-amounts">
                            <span className="suggestion-qty">Qté {itemQuantity}</span>
                            <span className="suggestion-price">{formatEuroFr(lineTotal)}</span>
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  <section className="synergy-section" aria-label="Synergies détectées">
                    <h5>
                      <img src="/Icon bolt.png" alt="" className="insight-title-icon" />
                      Synergies détectées
                    </h5>
                    {(optimizationResult.synergies || []).length === 0 ? (
                      <p className="synergy-empty">
                        Aucune relation pertinente n’a été établie entre les articles du panier.
                      </p>
                    ) : (
                      (optimizationResult.synergies || []).map((synergy, index) => (
                        <SynergyBlock
                          key={`${(synergy.items || []).join('-')}-${index}`}
                          synergy={synergy}
                          itemsMap={itemsMap}
                        />
                      ))
                    )}
                  </section>

                  {optimizationResult.duplicates?.length > 0 && (
                    <div className="insight-section">
                      <h5>
                        <img src="/Icon search.png" alt="" className="insight-title-icon" />
                        Doublons détectés
                      </h5>
                      {optimizationResult.duplicates.map((duplicate, index) => (
                        <div key={index} className="insight-item">
                          <InfoGlyph />
                          <span>
                            {duplicate.title ? (
                              <strong>{displayExplanatoryText(duplicate.title, duplicate.title_source)} </strong>
                            ) : null}
                            {displayExplanatoryText(
                              duplicate.reason || duplicate.description || duplicate.summary,
                              duplicate.reason_source || duplicate.description_source || duplicate.summary_source,
                            )}
                            {duplicate.recommendation ? (
                              <> {displayExplanatoryText(duplicate.recommendation, duplicate.recommendation_source)}</>
                            ) : null}
                            {duplicate.warning ? (
                              <> {displayExplanatoryText(duplicate.warning, duplicate.warning_source)}</>
                            ) : null}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {step === 'applying' && (
            <div className="optimization-loading">
              <div className="optimization-loading-stage is-apply" aria-hidden="true">
                {loadingAnimation && (
                  <Lottie
                    animationData={loadingAnimation}
                    loop={true}
                    style={{ width: 'min(42vw, 280px)', height: 'min(36vh, 240px)' }}
                  />
                )}
              </div>
              <div className="optimization-loading-copy">
                <h3>Application des modifications</h3>
                <p className="loading-hint">Veuillez patienter.</p>
              </div>
            </div>
          )}
        </div>

        {(step === 'input' || step === 'results') && (
          <div className="budget-opt-actions" data-protected-actions="true">
            {step === 'input' && (
              <>
                <LiquidGlassCancelButton onClick={handleClose}>
                  Annuler
                </LiquidGlassCancelButton>
                <LiquidGlassOptimizeButton onClick={handleOptimize} />
              </>
            )}

            {step === 'results' && (
              <>
                <LiquidGlassCancelButton onClick={() => setStep('input')}>
                  <img src="/Icon arrow back ios new.png" alt="Retour" className="back-icon" />
                  Retour
                </LiquidGlassCancelButton>
                <LiquidGlassApplyButton
                  onClick={handleApplyScenario}
                  disabled={!selectedScenario}
                  count={selectedSuggestions.size > 0 ? selectedSuggestions.size : selectedScenario?.suggestions?.length || 0}
                >
                  Appliquer
                </LiquidGlassApplyButton>
              </>
            )}
          </div>
        )}
      </div>
    </GlassOverlay>
  )
}

export default BudgetOptimizationModal
